// Sequential full-resolution frame access on a dedicated worker, for export and mask computation (P-11: the
// viewer keeps its own decoders).

import { SourceClient, type MediaInput } from './source-client';
import type { DecodedFrame } from './protocol';

/** Sequential frame feeder over a dedicated worker (export must not disturb the viewer, P-11). */
export class Feeder {
  private client: SourceClient;
  constructor(media: MediaInput) {
    this.client = new SourceClient(media);
    this.client.gpuLimit = 24;
  }
  async open(): Promise<void> {
    try {
      await this.client.open();
    } catch (e) {
      const err = e as { errorKey?: string; detail?: string };
      throw new Error(err.errorKey ? `${err.errorKey}: ${err.detail ?? ''}` : String(e), { cause: e });
    }
  }
  get info() {
    return this.client.info!;
  }
  /** Waits for a frame; retries once after a decoder error, rejects on a second error or when cancelled. */
  async get(index: number, ahead: number[], cancelled?: () => boolean): Promise<VideoFrame | DecodedFrame> {
    const c = this.client;
    const have = () => c.getGpuFrame(index) ?? c.getFrame(index);
    const got = have();
    if (got) {
      c.request([index, ...ahead], [index, ...ahead], 'frame');
      return got;
    }
    return new Promise((resolve, reject) => {
      let retried = false;
      const finish = (fn: () => void) => {
        clearTimeout(timer);
        clearInterval(poll);
        off();
        fn();
      };
      const timer = setTimeout(() => finish(() => reject(new Error(`Frame ${index} not decoded`))), 30000);
      const poll = setInterval(() => {
        if (cancelled?.()) finish(() => reject(new Error('cancelled')));
      }, 200);
      const off = c.addFrameListener((i) => {
        if (i !== index) return;
        const got = have();
        if (got) return finish(() => resolve(got));
        if (c.frameErrors.has(index)) {
          if (retried) return finish(() => reject(new Error(`Frame ${index}: ${c.frameErrors.get(index)}`)));
          retried = true;
          c.clearErrors();
          c.request([index, ...ahead], [index, ...ahead], 'frame');
        }
      });
      c.request([index, ...ahead], [index, ...ahead], 'frame');
    });
  }
  /**
   * Hands a VideoFrame of the frame to the caller, who must close (or transfer) it. Image sequences decode to planes
   * only; they are wrapped in an 8-bit RGBA VideoFrame here (mask computation, F-08).
   */
  take(index: number): VideoFrame | undefined {
    const vf = this.client.takeGpuFrame(index);
    if (vf) return vf;
    const f = this.client.getFrame(index);
    if (!f || (f.format !== 'RGBA' && f.format !== 'RGBA16')) return undefined;
    let bytes: Uint8Array<ArrayBuffer>;
    if (f.format === 'RGBA') bytes = new Uint8Array(f.data.slice(0));
    else {
      const s = new Uint16Array(f.data);
      bytes = new Uint8Array(s.length);
      for (let i = 0; i < s.length; i++) bytes[i] = s[i] >> 8;
    }
    return new VideoFrame(bytes, { format: 'RGBA', codedWidth: f.width, codedHeight: f.height, timestamp: Math.round(f.time * 1e6) });
  }

  release(before: number, after: number): void {
    this.client.retainGpu(before, after);
  }
  dispose(): void {
    this.client.dispose();
  }
}

